/**
 * Track Class
 * Represents a single audio track with metadata and audio processing nodes
 */
export class Track {
  constructor(id) {
    this.id = id;

    // Audio data. `audioBuffer` is how a locally-loaded file is held; a streamed
    // track will never have one, which is why the metadata below — not the
    // buffer — is what the rest of the library reads.
    this.audioBuffer = null;

    /** @type {import('../waveform/WaveformSource.js').WaveformSource|null} */
    this.waveform = null; // LocalWaveform or RemoteWaveform

    /** @type {import('./TrackSource.js').TrackSource|null} */
    this.source = null; // BufferTrackSource (local file) or StreamingTrackSource

    /** @type {import('../spectrogram/RemoteSpectrogram.js').RemoteSpectrogram|null} */
    this.spectrogram = null; // null when the asset has no spectrogram analysis
    this.spectrogramOpacity = 0; // 0 = waveform only, 1 = spectrogram fully covers it
    this.frequencyRange = null; // {min, max} to zoom the spectrogram; null = the whole axis
    // Tinted bands, [{min, max, color}]. Set DAW-wide today, but every track
    // holds its own copy and drawing reads only this — so per-track is a setter.
    this.frequencyHighlights = [];
    this.frequencyHighlightsOnLane = true; // false = on the frequency ruler only

    this.duration = 0; // Seconds
    this.sampleRate = 0; // Rate the audio data and waveform peaks are indexed in
    this.sampleCount = 0; // Length in `sampleRate` samples
    this.originalSampleRate = null; // Source file rate, before any browser resampling
    this.maxAmplitude = null; // Peak |sample| when known; may exceed 1.0 in float source

    this.timeOffset = 0; // Time offset in seconds (derived from sampleOffset)
    this.sampleOffset = 0; // Offset in the source file's sample rate (source of truth)
    this.pixelOffset = 0;

    this.gainNode = null; // Volume control (linear 0-1)
    this.preGainNode = null; // Gain control (dB boost/cut)
    this.compressorNode = null;
    this.limiterNode = null; // Always-on soft-clip ceiling, after gain/compressor
    this.panNode = null;

    this.fileName = "";
    this.color = "#2196F3";
    this.volume = 1.0; // Per-track volume (0.0 to 1.0)
    this.gain = 0; // Pre-gain in dB (-40 to +40)
    this.pan = 0; // Stereo pan (-1 = left, 0 = center, 1 = right)
    this.useCompressor = false; // Enable/disable compressor
    this.muted = false; // Silenced explicitly
    this.soloed = false; // Silences every track that isn't also soloed
    this.isLoading = false; // Overall loading state
    this.loadingPhase = null; // Specific phase: 'audio', 'waveform', or null
  }

  /**
   * Whether the track knows its own extent — enough to be positioned and drawn,
   * whether or not its audio is resident.
   * @returns {boolean}
   */
  hasAudio() {
    return this.sampleRate > 0 && this.duration > 0;
  }

  /**
   * Whether this track can be played right now. A remote track is drawable long
   * before it is playable — streamed playback arrives with the transport rework.
   * @returns {boolean}
   */
  canPlay() {
    return this.source !== null && this.source.isReady;
  }

  /**
   * The rate offsets are expressed in: the source file's own rate, which is not
   * necessarily the rate the decoded data ended up at. `decodeAudioData`
   * silently resamples to the AudioContext rate, and an offset measured against
   * that rate wouldn't survive a round trip through a different context.
   * @returns {number} Sample rate in Hz, or 0 if not yet known
   */
  getOffsetSampleRate() {
    return this.originalSampleRate || this.sampleRate;
  }

  /**
   * Attach decoded audio and derive the metadata from it.
   * @param {AudioBuffer} audioBuffer
   */
  setAudioBuffer(audioBuffer) {
    this.audioBuffer = audioBuffer;
    this.duration = audioBuffer.duration;
    this.sampleRate = audioBuffer.sampleRate;
    this.sampleCount = audioBuffer.length;
  }

  /**
   * Set the time offset and update sample offset to match
   * @param {number} timeOffset - Time offset in seconds
   */
  setTimeOffset(timeOffset) {
    this.timeOffset = timeOffset;
    const sampleRate = this.getOffsetSampleRate();
    if (sampleRate) {
      this.sampleOffset = Math.round(timeOffset * sampleRate);
    }
  }

  /**
   * Set the sample offset and update time offset to match
   * @param {number} sampleOffset - Sample offset in the source file's sample rate
   */
  setSampleOffset(sampleOffset) {
    this.sampleOffset = sampleOffset;
    const sampleRate = this.getOffsetSampleRate();
    if (sampleRate) {
      this.timeOffset = sampleOffset / sampleRate;
    }
  }
}
